// src/context/AprendizadoContext.tsx

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { db } from "services/firebase";
import { collection, addDoc, getDocs, onSnapshot, updateDoc, doc, deleteDoc, query, where } from "firebase/firestore";
import { useAuth } from "./AuthContext";

export interface AprendizadoItem {
  id: string;
  nome: string;
  dispositivoId: string;
  status: "Aprendendo" | "Operacional";
  dataCadastro: string;
  mediaPessoasPorDia: number;
}

interface AprendizadoContextType {
  aprendizadoData: AprendizadoItem[];
  adicionarAprendizado: (a: Omit<AprendizadoItem, "id">) => Promise<void>;
  atualizarAprendizado: (id: string, dados: Partial<AprendizadoItem>) => Promise<void>;
  removerPorDispositivo: (deviceId: string) => Promise<void>;
}

const AprendizadoContext = createContext<AprendizadoContextType | undefined>(undefined);

export const AprendizadoProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [aprendizadoData, setAprendizadoData] = useState<AprendizadoItem[]>([]);

  useEffect(() => {
    if (!user?.uid) {
      setAprendizadoData([]); // Limpa os dados se o usuário deslogar
      return;
    }

    const collectionPath = `clientes/${user.uid}/aprendizado`;
    const q = query(collection(db, collectionPath));

    const unsub = onSnapshot(q, snapshot => {
      const arr = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Omit<AprendizadoItem, "id">) }));
      setAprendizadoData(arr);
    });

    return () => unsub();
  }, [user]);

  const getCollectionRef = () => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return collection(db, `clientes/${user.uid}/aprendizado`);
  };

  const getDocRef = (id: string) => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return doc(db, `clientes/${user.uid}/aprendizado`, id);
  };

  const adicionarAprendizado = async (a: Omit<AprendizadoItem, "id">) => {
    await addDoc(getCollectionRef(), a);
  };

  const atualizarAprendizado = async (id: string, dados: Partial<AprendizadoItem>) => {
    await updateDoc(getDocRef(id), dados);
  };

  const removerPorDispositivo = async (deviceId: string) => {
    try {
      const q = query(getCollectionRef(), where("dispositivoId", "==", deviceId));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(docSnapshot => 
        deleteDoc(doc(getCollectionRef().firestore, getCollectionRef().path, docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Erro ao remover aprendizado por dispositivo:", error);
    }
  };

  return (
    <AprendizadoContext.Provider value={{ aprendizadoData, adicionarAprendizado, atualizarAprendizado, removerPorDispositivo }}>
      {children}
    </AprendizadoContext.Provider>
  );
};

export const useAprendizado = () => {
  const ctx = useContext(AprendizadoContext);
  if (!ctx) throw new Error("useAprendizado must be inside AprendizadoProvider");
  return ctx;
};
